import handleService from "../services/apiService.ts";
import { MethodsEnum } from "../enums/Methods.ts";
import { emitirNotificacao, TipoNotificacao } from "../contexts/NotificationContext.tsx";
import { formatarDataCirurgia } from "./relativeTime.util.ts";
import { StatusCirurgia } from "../ui/StatusBadge.tsx";

/**
 * Troca de status de cirurgia, compartilhada entre a listagem e a agenda.
 *
 * Estava só dentro de `pages/Cirurgias/listagemCirurgia.tsx`. Quando a agenda
 * ganhou a mesma ação, duplicar significaria que uma tela emitiria notificação e
 * a outra não — as duas passariam a divergir ao primeiro ajuste.
 */

export const STATUS_LABEL: Record<StatusCirurgia, string> = {
  AGENDADA: "Agendada",
  SEPARADO: "Separado",
  REALIZADA: "Realizada",
  CANCELADA: "Cancelada",
  ABERTO: "Aberto",
};

// CANCELADA/REALIZADA/AGENDADA já têm um tipo de notificação dedicado (mesmos
// usados na carga inicial, em NotificationContext.construirNotificacoes);
// SEPARADO/ABERTO caem no tipo genérico de troca de status.
export const tipoNotificacaoDoStatus = (status: StatusCirurgia): TipoNotificacao => {
  if (status === "CANCELADA") return "cirurgia_cancelada";
  if (status === "REALIZADA") return "cirurgia_realizada";
  if (status === "AGENDADA") return "cirurgia_agendada";
  return "cirurgia_status_alterado";
};

/**
 * CANCELADA vai por `DELETE /cirurgia/cancelar` com o motivo; os outros status
 * por `PATCH /cirurgia/status`. Quem chama decide o que recarregar depois.
 *
 * Erro não é tratado aqui de propósito: o interceptor do apiService já mostra a
 * mensagem. Quem chama só precisa do try/finally para soltar o estado de carga.
 */
export const alterarStatusCirurgia = async (
  id: string,
  status: StatusCirurgia,
  motivoTexto = "",
  link: "/cirurgias" | "/agenda" = "/cirurgias"
) => {
  const cancelando = status === "CANCELADA";
  const response = await handleService({
    method: MethodsEnum[cancelando ? "DELETE" : "PATCH"],
    endpoint: `/cirurgia/${cancelando ? "cancelar" : "status"}`,
    data: cancelando ? { id, motivo: motivoTexto } : { id, status },
  });

  const cirurgia = response?.data;
  if (cirurgia?.id) {
    const quando = formatarDataCirurgia(cirurgia.data);
    const descricao = cancelando
      ? `A cirurgia de ${cirurgia.paciente}, marcada para ${quando}, foi cancelada${motivoTexto ? ` — ${motivoTexto}` : "."}`
      : status === "REALIZADA"
      ? `A cirurgia de ${cirurgia.paciente} (${quando}) foi marcada como realizada.`
      : status === "AGENDADA"
      ? `Cirurgia de ${cirurgia.paciente} marcada para ${quando} — ${cirurgia.hospital}.`
      : `A cirurgia de ${cirurgia.paciente} (${quando}) foi atualizada para "${STATUS_LABEL[status]}".`;

    emitirNotificacao({
      id: `cirurgia-${cirurgia.id}-${status.toLowerCase()}-${Date.now()}`,
      tipo: tipoNotificacaoDoStatus(status),
      titulo: cancelando
        ? "Cirurgia cancelada"
        : status === "REALIZADA"
        ? "Cirurgia concluída"
        : status === "AGENDADA"
        ? "Cirurgia agendada"
        : "Status atualizado",
      descricao,
      link,
    });
  }

  return response;
};
